/**
 * IVA Inteligente - Dashboard y Detalle de Movimientos
 * Vista unificada de control de IVA con categorización automática
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle2,
  Calculator,
  Zap,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  FileText,
  Percent,
  Info,
  ArrowUpRight,
  ArrowDownRight,
  List,
  LayoutDashboard,
  ChevronDown,
  ChevronUp,
  Edit3,
  Check,
  X as XIcon,
  BarChart3,
  AlertCircle,
  FileSpreadsheet,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { gastosApi } from '../../utils/api';
import LoadingSpinner, { LoadingPage } from '../../components/LoadingSpinner';
import MonthRangePicker from '../../components/MonthRangePicker';
import EmptyState from '../../components/EmptyState';
import useAuthStore from '../../store/authStore';
import clsx from 'clsx';

// Opciones de categorización
const TIPOS_MOVIMIENTO = [
  { value: '01', label: 'Compra Local', desc: 'Gravada con IVA' },
  { value: '02', label: 'Importación', desc: 'Servicios extranjero' },
  { value: '03', label: 'No Deducible', desc: 'IVA como gasto' }
];

const CLASES = [
  { value: 'B', label: 'Bienes', desc: 'Mercadería, insumos' },
  { value: 'S', label: 'Servicios', desc: 'Servicios profesionales' },
  { value: 'K', label: 'Capital', desc: 'Activos fijos' }
];

const IDENTIFICACIONES = [
  { value: 'E', label: 'Identificado', desc: '100% acreditable' },
  { value: 'N', label: 'No Identificado', desc: 'Aplica prorrata' }
];

// Componente de árbol jerárquico D-104 para IVA Créditos (siempre expandido)
// Pulso palpitante: avisa que en este período hay anulaciones de mes cruzado
// (FE anulada por NC de otro mes, o NC de este mes que anula FE de otro mes).
function AnulacionesCruzadasPulse({ periodo, formatCurrency }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        const res = await gastosApi.getAnulacionesCruzadas(periodo);
        if (activo) setData(res.data?.data || null);
      } catch { if (activo) setData(null); }
    })();
    return () => { activo = false; };
  }, [periodo]);

  if (!data || data.count === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold border border-amber-300 hover:bg-amber-200"
        title="Anulaciones de mes cruzado detectadas"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
        </span>
        {data.count} anulación{data.count === 1 ? '' : 'es'} de mes cruzado
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl border border-amber-200 w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b bg-amber-50 rounded-t-xl">
              <div className="flex items-center gap-2 font-semibold text-amber-800 text-sm">
                <AlertTriangle className="h-4 w-4" /> Anulaciones de mes cruzado
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700"><XIcon className="h-4 w-4" /></button>
            </div>
            <div className="px-4 py-3 overflow-y-auto text-xs">
              <p className="text-gray-500 mb-3 leading-snug">
                La FE y su NC caen en meses distintos. Por eso el Débito por período (este reporte) difiere de la vista neta de Historial — cada documento se declara en su propio mes.
              </p>
              <div className="space-y-2.5">
                {data.items.map((it, i) => (
                  <div key={i} className="border-b border-gray-100 pb-2.5 last:border-0">
                    <div className="font-mono text-gray-800">{it.tipo === 'fe_de_este_mes' ? it.feConsecutivo : it.ncConsecutivo}</div>
                    <div className="text-gray-600">{it.cliente}</div>
                    <div className="text-amber-700 mt-0.5">
                      {it.tipo === 'fe_de_este_mes'
                        ? <>FE de <b>{it.feMes}</b> anulada por NC de <b>{it.ncMes}</b></>
                        : <>NC de <b>{it.ncMes}</b> que anula FE de <b>{it.feMes}</b></>}
                    </div>
                    <div className="text-gray-500">IVA: {formatCurrency ? formatCurrency(it.ivaCrc) : it.ivaCrc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function ArbolD104Creditos({ periodo, formatCurrency }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const response = await gastosApi.getControlIvaAnalisis({ periodo });
        if (response.data.success) {
          setData(response.data.data);
        }
      } catch (error) {
        console.error('Error loading analysis:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [periodo]);

  // Componente de fila recursivo (5 columnas con Exoneración)
  const TreeRow = ({ item, level, isLast = false }) => {
    const indent = level * 16;
    const isNC = item.nombre?.includes('Nota de Crédito');
    const isNegative = (item.totals?.baseImponible || 0) < 0;
    const hasChildren = item.children && item.children.length > 0;

    const montoExoneracion = item.totals?.montoExoneracion || 0;
    const montoIva = item.totals?.montoIva || 0;
    const ivaAcreditar = item.totals?.ivaAcreditar || 0;

    const levelStyles = {
      0: 'font-medium text-gray-900',
      1: 'text-gray-800',
      2: 'text-gray-600',
      3: 'text-gray-500'
    };

    return (
      <>
        <tr className={`${levelStyles[level] || ''} hover:bg-gray-50`}>
          <td className="py-1 border-b border-gray-100">
            <div className="flex items-center" style={{ paddingLeft: `${indent + 8}px` }}>
              {level > 0 && (
                <span className="text-gray-300 mr-1 text-[10px]">
                  {isLast ? '└' : '├'}
                </span>
              )}
              <span className={`truncate ${isNC || isNegative ? 'text-red-600' : ''}`} title={item.nombre}>
                {item.nombre}
              </span>
            </div>
          </td>
          <td className={`px-2 py-1 text-right border-b border-gray-100 ${isNC || isNegative ? 'text-red-600' : ''}`}>
            {isNegative ? `(${formatCurrency(Math.abs(item.totals?.baseImponible || 0))})` : formatCurrency(item.totals?.baseImponible || 0)}
          </td>
          <td className={`px-2 py-1 text-right border-b border-gray-100 ${isNC || isNegative ? 'text-red-600' : ''}`}>
            {formatCurrency(montoExoneracion)}
          </td>
          <td className={`px-2 py-1 text-right border-b border-gray-100 ${isNC || isNegative ? 'text-red-600' : ''}`}>
            {isNegative ? `(${formatCurrency(Math.abs(montoIva))})` : formatCurrency(montoIva)}
          </td>
          <td className={`px-2 py-1 text-right border-b border-gray-100 font-medium ${isNC || isNegative ? 'text-red-600' : 'text-emerald-600'}`}>
            {isNegative ? `(${formatCurrency(Math.abs(ivaAcreditar))})` : formatCurrency(ivaAcreditar)}
          </td>
        </tr>
        {hasChildren && item.children.map((child, idx) => (
          <TreeRow
            key={`${level}-${idx}`}
            item={child}
            level={level + 1}
            isLast={idx === item.children.length - 1}
          />
        ))}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-emerald-500 animate-spin" />
      </div>
    );
  }

  // Si no hay datos, mostrar estructura vacía con ceros
  const isEmpty = !data?.hierarchy?.length;

  const grandTotals = isEmpty ? {
    baseImponible: 0,
    montoExoneracion: 0,
    montoIva: 0,
    ivaAcreditar: 0
  } : data.hierarchy.reduce((acc, act) => ({
    baseImponible: acc.baseImponible + (act.totals?.baseImponible || 0),
    montoExoneracion: acc.montoExoneracion + (act.totals?.montoExoneracion || 0),
    montoIva: acc.montoIva + (act.totals?.montoIva || 0),
    ivaAcreditar: acc.ivaAcreditar + (act.totals?.ivaAcreditar || 0)
  }), { baseImponible: 0, montoExoneracion: 0, montoIva: 0, ivaAcreditar: 0 });

  return (
    <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl h-full">
      <div className="min-w-max">
      <div className="px-3 py-2 bg-emerald-50 border-b border-gray-200">
        <h4 className="font-semibold text-gray-900 text-sm">Crédito Fiscal - Compras</h4>
        <p className="text-xs text-gray-500">IVA acreditable</p>
        {data?.actividadEconomica && (
          <p className="text-xs text-emerald-600 mt-1 truncate" title={data.actividadEconomica}>
            {data.actividadEconomica}
          </p>
        )}
      </div>
      <table className="w-full text-xs">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b border-gray-200">
              Categoría
            </th>
            <th className="px-2 py-2 text-right font-semibold text-gray-700 border-b border-gray-200">
              Base Imp.
            </th>
            <th className="px-2 py-2 text-right font-semibold text-gray-700 border-b border-gray-200">
              Exoneración
            </th>
            <th className="px-2 py-2 text-right font-semibold text-gray-700 border-b border-gray-200">
              IVA
            </th>
            <th className="px-2 py-2 text-right font-semibold text-emerald-600 border-b border-gray-200">
              A Crédito
            </th>
          </tr>
        </thead>
        <tbody>
          {isEmpty ? (
            <tr className="text-gray-400">
              <td className="py-2 px-2 border-b border-gray-100 italic">Sin compras en este período</td>
              <td className="py-2 px-2 text-right border-b border-gray-100">₡0,00</td>
              <td className="py-2 px-2 text-right border-b border-gray-100">₡0,00</td>
              <td className="py-2 px-2 text-right border-b border-gray-100">₡0,00</td>
              <td className="py-2 px-2 text-right border-b border-gray-100">₡0,00</td>
            </tr>
          ) : (
            data.hierarchy.map((item, idx, arr) => (
              <TreeRow
                key={idx}
                item={item}
                level={0}
                isLast={idx === arr.length - 1}
              />
            ))
          )}
        </tbody>
        <tfoot className="bg-emerald-50 font-bold border-t-2 border-emerald-200">
          <tr>
            <td className="px-2 py-2 text-gray-900 text-sm">TOTAL</td>
            <td className="px-2 py-2 text-right text-gray-700">
              {formatCurrency(grandTotals.baseImponible)}
            </td>
            <td className="px-2 py-2 text-right text-gray-700">
              {formatCurrency(grandTotals.montoExoneracion)}
            </td>
            <td className="px-2 py-2 text-right text-gray-700">
              {formatCurrency(grandTotals.montoIva)}
            </td>
            <td className="px-2 py-2 text-right text-emerald-700 text-sm">
              {formatCurrency(grandTotals.ivaAcreditar)}
            </td>
          </tr>
        </tfoot>
      </table>
      {data?.uncategorized?.cantidad_lineas > 0 && (
        <div className="m-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
          <AlertCircle className="w-3 h-3 inline mr-1" />
          {data.uncategorized.cantidad_lineas} líneas sin categorizar
        </div>
      )}
      <div className="m-2 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs">
        <p className="text-gray-600">
          <span className="font-medium">{data?.summary?.total_facturas || 0}</span> documentos procesados en este período
        </p>
      </div>
      </div>
    </div>
  );
}

// Componente de árbol jerárquico D-104 para FEC Autoliquidación y Solo Crédito
function ArbolD104FEC({ periodo, formatCurrency }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const response = await gastosApi.getDetalleFEC(periodo);
        if (response.data.success) {
          setData(response.data.data);
        }
      } catch (error) {
        console.error('Error loading FEC:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [periodo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-purple-500 animate-spin" />
      </div>
    );
  }

  if (!data?.facturas?.length) {
    return (
      <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl h-full">
        <div className="min-w-max">
        <div className="px-3 py-2 bg-purple-50 border-b border-gray-200">
          <h4 className="font-semibold text-gray-900 text-sm">FEC - Control Fiscal</h4>
          <p className="text-xs text-gray-500">Proveedores sin FE</p>
          {data?.actividadEconomica && (
            <p className="text-xs text-purple-600 mt-1 truncate" title={data.actividadEconomica}>
              {data.actividadEconomica}
            </p>
          )}
        </div>
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b border-gray-200">
                Categoría
              </th>
              <th className="px-2 py-2 text-right font-semibold text-red-600 border-b border-gray-200">
                Débito Fiscal
              </th>
              <th className="px-2 py-2 text-right font-semibold text-emerald-600 border-b border-gray-200">
                Crédito Fiscal
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Autoliquidación IVA vacío */}
            <tr className="font-medium text-gray-900 hover:bg-gray-50">
              <td className="py-1 border-b border-gray-100">
                <div className="flex items-center pl-2">
                  <span>Autoliquidación IVA</span>
                </div>
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-red-600">
                {formatCurrency(0)}
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                {formatCurrency(0)}
              </td>
            </tr>
            <tr className="text-gray-800 hover:bg-gray-50">
              <td className="py-1 border-b border-gray-100">
                <div className="flex items-center" style={{ paddingLeft: '24px' }}>
                  <span className="text-gray-300 mr-1 text-[10px]">├</span>
                  <span>Tarifa general 13%</span>
                </div>
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-red-600">
                {formatCurrency(0)}
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                {formatCurrency(0)}
              </td>
            </tr>
            <tr className="text-gray-600 hover:bg-gray-50">
              <td className="py-1 border-b border-gray-100">
                <div className="flex items-center" style={{ paddingLeft: '40px' }}>
                  <span className="text-gray-300 mr-1 text-[10px]">└</span>
                  <span>Factura Electrónica de Compra (0)</span>
                </div>
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-red-600">
                {formatCurrency(0)}
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                {formatCurrency(0)}
              </td>
            </tr>
            {/* IVA Transfronterizo vacío */}
            <tr className="font-medium text-gray-900 hover:bg-gray-50 bg-blue-50/50">
              <td className="py-1 border-b border-gray-100">
                <div className="flex items-center pl-2">
                  <span>IVA Transfronterizo</span>
                </div>
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-400">
                -
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                {formatCurrency(0)}
              </td>
            </tr>
            <tr className="text-gray-800 hover:bg-gray-50">
              <td className="py-1 border-b border-gray-100">
                <div className="flex items-center" style={{ paddingLeft: '24px' }}>
                  <span className="text-gray-300 mr-1 text-[10px]">├</span>
                  <span>Tarifa general 13%</span>
                </div>
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-400">
                -
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                {formatCurrency(0)}
              </td>
            </tr>
            <tr className="text-gray-600 hover:bg-gray-50">
              <td className="py-1 border-b border-gray-100">
                <div className="flex items-center" style={{ paddingLeft: '40px' }}>
                  <span className="text-gray-300 mr-1 text-[10px]">└</span>
                  <span>FEC Solo Crédito (0)</span>
                </div>
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-400">
                -
              </td>
              <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                {formatCurrency(0)}
              </td>
            </tr>
          </tbody>
          <tfoot className="bg-purple-100">
            <tr className="font-bold text-gray-900">
              <td className="px-2 py-2 border-t border-gray-300">TOTAL</td>
              <td className="px-2 py-2 text-right border-t border-gray-300 text-red-700">
                {formatCurrency(0)}
              </td>
              <td className="px-2 py-2 text-right border-t border-gray-300 text-emerald-700">
                {formatCurrency(0)}
              </td>
            </tr>
            <tr className="text-gray-600 text-[10px]">
              <td colSpan={3} className="px-2 py-1">
                0 documentos procesados en este período
              </td>
            </tr>
          </tfoot>
        </table>
        </div>
      </div>
    );
  }

  // Separar FECs por tipo: autoliquidación vs solo crédito (IVA transfronterizo)
  const fecsAutoliquidacion = data.facturas.filter(f => !f.fec_solo_credito);
  const fecsSoloCredito = data.facturas.filter(f => f.fec_solo_credito);

  // Calcular totales por tipo
  const totalDebitoAuto = fecsAutoliquidacion.reduce((acc, f) => acc + (parseFloat(f.debito_crc) || 0), 0);
  const totalCreditoAuto = fecsAutoliquidacion.reduce((acc, f) => acc + (parseFloat(f.credito_crc) || 0), 0);
  const totalCreditoSolo = fecsSoloCredito.reduce((acc, f) => acc + (parseFloat(f.credito_crc) || 0), 0);
  // Base imponible en CRC (para totalizar compras: locales + FEC/internacionales)
  const totalBaseAuto = fecsAutoliquidacion.reduce((acc, f) => acc + (parseFloat(f.base_imponible_crc) || 0), 0);
  const totalBaseSolo = fecsSoloCredito.reduce((acc, f) => acc + (parseFloat(f.base_imponible_crc) || 0), 0);
  const totalBase = totalBaseAuto + totalBaseSolo;

  // Totales globales
  const totalDebito = totalDebitoAuto; // Solo autoliquidación genera débito
  const totalCredito = totalCreditoAuto + totalCreditoSolo;
  // Saldo: positivo = a favor (más crédito), negativo = a pagar (más débito)
  const saldoNeto = totalCredito - totalDebito;

  return (
    <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl h-full">
      <div className="min-w-max">
      <div className="px-3 py-2 bg-purple-50 border-b border-gray-200">
        <h4 className="font-semibold text-gray-900 text-sm">FEC - Control Fiscal</h4>
        <p className="text-xs text-gray-500">Proveedores sin FE</p>
        {data.actividadEconomica && (
          <p className="text-xs text-purple-600 mt-1 truncate" title={data.actividadEconomica}>
            {data.actividadEconomica}
          </p>
        )}
      </div>
      <table className="w-full text-xs">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b border-gray-200">
              Categoría
            </th>
            <th className="px-2 py-2 text-right font-semibold text-gray-700 border-b border-gray-200">
              Base CRC
            </th>
            <th className="px-2 py-2 text-right font-semibold text-red-600 border-b border-gray-200">
              Débito Fiscal
            </th>
            <th className="px-2 py-2 text-right font-semibold text-emerald-600 border-b border-gray-200">
              Crédito Fiscal
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Sección 1: Autoliquidación IVA (débito = crédito) */}
          {fecsAutoliquidacion.length > 0 && (
            <>
              <tr className="font-medium text-gray-900 hover:bg-gray-50">
                <td className="py-1 border-b border-gray-100">
                  <div className="flex items-center pl-2">
                    <span>Autoliquidación IVA</span>
                  </div>
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-800">
                  {formatCurrency(totalBaseAuto)}
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-red-600">
                  {formatCurrency(totalDebitoAuto)}
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                  {formatCurrency(totalCreditoAuto)}
                </td>
              </tr>
              <tr className="text-gray-800 hover:bg-gray-50">
                <td className="py-1 border-b border-gray-100">
                  <div className="flex items-center" style={{ paddingLeft: '24px' }}>
                    <span className="text-gray-300 mr-1 text-[10px]">├</span>
                    <span>Tarifa general 13%</span>
                  </div>
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-800">
                  {formatCurrency(totalBaseAuto)}
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-red-600">
                  {formatCurrency(totalDebitoAuto)}
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                  {formatCurrency(totalCreditoAuto)}
                </td>
              </tr>
              <tr className="text-gray-600 hover:bg-gray-50">
                <td className="py-1 border-b border-gray-100">
                  <div className="flex items-center" style={{ paddingLeft: '40px' }}>
                    <span className="text-gray-300 mr-1 text-[10px]">└</span>
                    <span>Factura Electrónica de Compra ({fecsAutoliquidacion.length})</span>
                  </div>
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-800">
                  {formatCurrency(totalBaseAuto)}
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-red-600">
                  {formatCurrency(totalDebitoAuto)}
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                  {formatCurrency(totalCreditoAuto)}
                </td>
              </tr>
            </>
          )}

          {/* Sección 2: Solo Crédito - IVA Transfronterizo (banco ya retuvo) */}
          {fecsSoloCredito.length > 0 && (
            <>
              <tr className="font-medium text-gray-900 hover:bg-gray-50 bg-blue-50/50">
                <td className="py-1 border-b border-gray-100">
                  <div className="flex items-center pl-2">
                    <span>IVA Transfronterizo</span>
                  </div>
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-800">
                  {formatCurrency(totalBaseSolo)}
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-400">
                  -
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                  {formatCurrency(totalCreditoSolo)}
                </td>
              </tr>
              <tr className="text-gray-800 hover:bg-gray-50">
                <td className="py-1 border-b border-gray-100">
                  <div className="flex items-center" style={{ paddingLeft: '24px' }}>
                    <span className="text-gray-300 mr-1 text-[10px]">├</span>
                    <span>Tarifa general 13%</span>
                  </div>
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-800">
                  {formatCurrency(totalBaseSolo)}
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-400">
                  -
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                  {formatCurrency(totalCreditoSolo)}
                </td>
              </tr>
              <tr className="text-gray-600 hover:bg-gray-50">
                <td className="py-1 border-b border-gray-100">
                  <div className="flex items-center" style={{ paddingLeft: '40px' }}>
                    <span className="text-gray-300 mr-1 text-[10px]">└</span>
                    <span>Factura Electrónica de Compra - Crédito ({fecsSoloCredito.length})</span>
                  </div>
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-800">
                  {formatCurrency(totalBaseSolo)}
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-gray-400">
                  -
                </td>
                <td className="px-2 py-1 text-right border-b border-gray-100 text-emerald-600">
                  {formatCurrency(totalCreditoSolo)}
                </td>
              </tr>
            </>
          )}
        </tbody>
        <tfoot className="bg-purple-50 font-bold border-t-2 border-purple-200">
          <tr>
            <td className="px-2 py-2 text-gray-900 text-sm">TOTAL</td>
            <td className="px-2 py-2 text-right text-gray-900">
              {formatCurrency(totalBase)}
            </td>
            <td className="px-2 py-2 text-right text-red-600">
              {formatCurrency(totalDebito)}
            </td>
            <td className="px-2 py-2 text-right text-emerald-600">
              {formatCurrency(totalCredito)}
            </td>
          </tr>
          <tr className="bg-gray-100">
            <td className="px-2 py-1.5 text-gray-700 text-sm">Saldo Neto</td>
            <td colSpan={3} className={`px-2 py-1.5 text-center font-bold ${saldoNeto > 0 ? 'text-emerald-700' : saldoNeto < 0 ? 'text-red-700' : 'text-purple-700'}`}>
              {saldoNeto === 0 ? '₡0' : (
                <span>
                  {formatCurrency(Math.abs(saldoNeto))}
                  <span className="text-xs font-normal ml-1">
                    {saldoNeto > 0 ? 'a favor' : 'a pagar'}
                  </span>
                </span>
              )}
            </td>
          </tr>
        </tfoot>
      </table>
      <div className="m-2 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs">
        <p className="text-gray-600">
          <span className="font-medium">{data.facturas?.length || 0}</span> FECs procesadas
          {fecsSoloCredito.length > 0 && (
            <span className="text-blue-600 ml-1">
              ({fecsSoloCredito.length} con IVA retenido por banco)
            </span>
          )}
        </p>
      </div>
      </div>
    </div>
  );
}

// Componente de árbol jerárquico D-104 para IVA Débitos (siempre expandido)
function ArbolD104Debitos({ periodo, formatCurrency }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const response = await gastosApi.getControlIvaDebitos({ periodo });
        if (response.data.success) {
          setData(response.data.data);
        }
      } catch (error) {
        console.error('Error loading débitos analysis:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [periodo]);

  // Componente de fila recursivo (4 columnas como FinanzaPro)
  const TreeRow = ({ item, level, isLast = false }) => {
    const indent = level * 16;
    const isNC = item.nombre?.includes('Nota de Crédito');
    const isNegative = (item.totals?.baseImponible || 0) < 0;
    const hasChildren = item.children && item.children.length > 0;

    // Valores del backend
    const montoExoneracion = item.totals?.montoExoneracion || 0;
    const montoIva = item.totals?.montoIva || item.totals?.ivaDebito || 0;  // IVA cobrado (0 para exonerados)

    const levelStyles = {
      0: 'font-medium text-gray-900',
      1: 'text-gray-800',
      2: 'text-gray-600',
      3: 'text-gray-500'
    };

    return (
      <>
        <tr className={`${levelStyles[level] || ''} hover:bg-gray-50`}>
          <td className="py-1 border-b border-gray-100">
            <div className="flex items-center" style={{ paddingLeft: `${indent + 8}px` }}>
              {level > 0 && (
                <span className="text-gray-300 mr-1 text-[10px]">
                  {isLast ? '└' : '├'}
                </span>
              )}
              <span className={`truncate ${isNC || isNegative ? 'text-green-600' : ''}`} title={item.nombre}>
                {item.nombre}
              </span>
            </div>
          </td>
          <td className={`px-2 py-1 text-right border-b border-gray-100 ${isNC || isNegative ? 'text-green-600' : ''}`}>
            {isNegative ? `(${formatCurrency(Math.abs(item.totals?.baseImponible || 0))})` : formatCurrency(item.totals?.baseImponible || 0)}
          </td>
          <td className={`px-2 py-1 text-right border-b border-gray-100 ${isNC || isNegative ? 'text-green-600' : ''}`}>
            {formatCurrency(montoExoneracion)}
          </td>
          <td className={`px-2 py-1 text-right border-b border-gray-100 ${isNC || isNegative ? 'text-green-600' : ''}`}>
            {isNegative ? `(${formatCurrency(Math.abs(montoIva))})` : formatCurrency(montoIva)}
          </td>
          <td className={`px-2 py-1 text-right border-b border-gray-100 font-medium ${isNC || isNegative ? 'text-green-600' : 'text-red-600'}`}>
            {isNegative ? `(${formatCurrency(Math.abs(montoIva))})` : formatCurrency(montoIva)}
          </td>
        </tr>
        {hasChildren && item.children.map((child, idx) => (
          <TreeRow
            key={`${level}-${idx}`}
            item={child}
            level={level + 1}
            isLast={idx === item.children.length - 1}
          />
        ))}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-red-500 animate-spin" />
      </div>
    );
  }

  if (!data?.hierarchy?.length) {
    return (
      <div className="p-8 text-center text-gray-500">
        <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">No hay datos de ventas</p>
        <p className="text-sm mt-1">Las ventas emitidas aparecerán aquí automáticamente</p>
      </div>
    );
  }

  const grandTotals = data.hierarchy.reduce((acc, act) => {
    const exoneracion = act.totals?.montoExoneracion || 0;
    const montoIva = act.totals?.montoIva || act.totals?.ivaDebito || 0;
    return {
      baseImponible: acc.baseImponible + (act.totals?.baseImponible || 0),
      montoExoneracion: acc.montoExoneracion + exoneracion,
      montoIva: acc.montoIva + montoIva  // IVA cobrado (0 para exonerados)
    };
  }, { baseImponible: 0, montoExoneracion: 0, montoIva: 0 });

  return (
    <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl h-full">
      <div className="min-w-max">
      <div className="px-3 py-2 bg-red-50 border-b border-gray-200">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h4 className="font-semibold text-gray-900 text-sm">Débito Fiscal - Ventas</h4>
          <AnulacionesCruzadasPulse periodo={periodo} formatCurrency={formatCurrency} />
        </div>
        <p className="text-xs text-gray-500">IVA cobrado</p>
        {data.actividadEconomica && (
          <p className="text-xs text-red-600 mt-1 truncate" title={data.actividadEconomica}>
            {data.actividadEconomica}
          </p>
        )}
      </div>
      <table className="w-full text-xs">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b border-gray-200">
              Categoría
            </th>
            <th className="px-2 py-2 text-right font-semibold text-gray-700 border-b border-gray-200">
              Base Imp.
            </th>
            <th className="px-2 py-2 text-right font-semibold text-gray-700 border-b border-gray-200">
              Exoneración
            </th>
            <th className="px-2 py-2 text-right font-semibold text-gray-700 border-b border-gray-200">
              IVA
            </th>
            <th className="px-2 py-2 text-right font-semibold text-red-600 border-b border-gray-200">
              A Débito
            </th>
          </tr>
        </thead>
        <tbody>
          {data.hierarchy.flatMap((activity) => activity.children || []).map((item, idx, arr) => (
            <TreeRow
              key={idx}
              item={item}
              level={0}
              isLast={idx === arr.length - 1}
            />
          ))}
        </tbody>
        <tfoot className="bg-red-50 font-bold border-t-2 border-red-200">
          <tr>
            <td className="px-2 py-2 text-gray-900 text-sm">TOTAL</td>
            <td className="px-2 py-2 text-right text-gray-700">
              {formatCurrency(grandTotals.baseImponible)}
            </td>
            <td className="px-2 py-2 text-right text-gray-700">
              {formatCurrency(grandTotals.montoExoneracion)}
            </td>
            <td className="px-2 py-2 text-right text-gray-700">
              {formatCurrency(grandTotals.montoIva)}
            </td>
            <td className="px-2 py-2 text-right text-red-700 text-sm">
              {formatCurrency(grandTotals.montoIva)}
            </td>
          </tr>
        </tfoot>
      </table>
      {data.summary && (
        <div className="m-2 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs">
          <p className="text-gray-600">
            <span className="font-medium">{data.summary.total_documentos || 0}</span> documentos procesados en este período
          </p>
        </div>
      )}
      </div>
    </div>
  );
}

export default function IVAInteligente() {
  const { currentEmisor } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [categorizando, setCategorizando] = useState(false);
  const [data, setData] = useState(null);
  const [vista, setVista] = useState('dashboard'); // 'dashboard' | 'debitos' | 'creditos' | 'fec'
  const [detalleData, setDetalleData] = useState(null);
  const [expandedFactura, setExpandedFactura] = useState(null);
  const [editingLinea, setEditingLinea] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [exportingDetalle, setExportingDetalle] = useState(null); // 'debitos-excel' etc.
  // Estado expandido/colapsado de los 5 grupos por categoría tributaria.
  // Orden: gravadas13 (paga IVA completo), tarifaReducida (1%/2%/4%),
  // exoneradas (con autorización), exentas (legalmente sin IVA),
  // noSujetas (Tarifa 0% sin derecho a crédito, código '11').
  const [grupoExpandido, setGrupoExpandido] = useState({
    deb_gravadas13: true, deb_reducidas: true, deb_exoneradas: true, deb_exentas: true, deb_noSujetas: true,
    cred_gravadas13: true, cred_reducidas: true, cred_exoneradas: true, cred_exentas: true, cred_noSujetas: true
  });
  // Grupos dinámicos (por tarifa) arrancan expandidos: si la clave no existe, se considera abierta.
  const toggleGrupo = (k) => setGrupoExpandido(prev => ({ ...prev, [k]: prev[k] === undefined ? false : !prev[k] }));
  const [periodo, setPeriodo] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  /**
   * Clasifica un documento en una de 5 categorías tributarias.
   * Prioridad: noSujeta > exonerada > exenta > tarifaReducida > gravada13.
   * - noSujeta: tarifa 0 y total_no_sujeto > 0 (código '11' Hacienda).
   * - exonerada: tiene exoneración > 0 (con o sin tarifa reducida).
   * - exenta: tarifa 0 y total_exento > 0.
   * - reducida: tarifa 1, 2 o 4 (canasta básica, parauniversitarias, prensa).
   * - gravada13: tarifa 13% (o cualquier > 4 sin exoneración).
   */
  const clasificarCategoria = (d) => {
    const tarifa = parseFloat(d.tarifa) || 0;
    const exoneracion = parseFloat(d.exoneracion_crc) || 0;
    if (exoneracion > 0.01) return 'exoneradas';
    if (tarifa === 0) {
      // total_no_sujeto se mapea a tipo_documento_tarifa = '11' en BD
      const totalNoSujeto = parseFloat(d.total_no_sujeto) || 0;
      const totalExento = parseFloat(d.total_exento) || 0;
      if (totalNoSujeto > 0 && totalExento === 0) return 'noSujetas';
      if (totalNoSujeto > 0) return 'noSujetas';  // mixto cae como no sujeta (más restrictivo)
      return 'exentas';
    }
    if (tarifa === 1 || tarifa === 2 || tarifa === 4) return 'reducidas';
    return 'gravadas13';
  };

  /**
   * Particiona un listado de docs en 5 grupos por categoría tributaria.
   * NC se restan del subtotal de su grupo.
   */
  const particionarPorCategoria = (docs) => {
    const acc = () => ({ base: 0, exo: 0, iva: 0, neto: 0, cantFE: 0, cantNC: 0 });
    const grupos = {
      gravadas13: { docs: [], sub: acc() },
      reducidas: { docs: [], sub: acc() },
      exoneradas: { docs: [], sub: acc() },
      exentas: { docs: [], sub: acc() },
      noSujetas: { docs: [], sub: acc() }
    };
    docs.forEach(d => {
      const cat = clasificarCategoria(d);
      const mult = d.esNC ? -1 : 1;
      grupos[cat].docs.push(d);
      grupos[cat].sub.base += (parseFloat(d.base_imponible_crc) || 0) * mult;
      grupos[cat].sub.exo += (parseFloat(d.exoneracion_crc) || 0) * mult;
      grupos[cat].sub.iva += (parseFloat(d.impuesto_crc) || 0) * mult;
      grupos[cat].sub.neto += (parseFloat(d.iva_acreditable_crc) || parseFloat(d.impuesto_crc) || 0) * mult;
      if (d.esNC) grupos[cat].sub.cantNC++; else grupos[cat].sub.cantFE++;
    });
    return grupos;
  };

  // Metadata por categoría (icono/color/label/descripción).
  const CATEGORIA_META = {
    gravadas13: { label: 'GRAVADAS 13%', desc: 'IVA completo', emoji: '🟢', bg: 'bg-emerald-50', bgHover: 'hover:bg-emerald-100', border: 'border-emerald-200', text: 'text-emerald-900', textSub: 'text-emerald-800' },
    reducidas: { label: 'TARIFA REDUCIDA', desc: '1% / 2% / 4% (canasta básica, agrícola, prensa)', emoji: '🟡', bg: 'bg-amber-50', bgHover: 'hover:bg-amber-100', border: 'border-amber-200', text: 'text-amber-900', textSub: 'text-amber-800' },
    exoneradas: { label: 'EXONERADAS', desc: 'Con autorización especial (UTN, ZF, etc.)', emoji: '🔵', bg: 'bg-blue-50', bgHover: 'hover:bg-blue-100', border: 'border-blue-200', text: 'text-blue-900', textSub: 'text-blue-800' },
    exentas: { label: 'EXENTAS', desc: '0% legalmente exento', emoji: '⚪', bg: 'bg-gray-50', bgHover: 'hover:bg-gray-100', border: 'border-gray-200', text: 'text-gray-900', textSub: 'text-gray-700' },
    noSujetas: { label: 'NO SUJETAS', desc: "Tarifa 0% sin derecho a crédito (cód. '11')", emoji: '🟣', bg: 'bg-purple-50', bgHover: 'hover:bg-purple-100', border: 'border-purple-200', text: 'text-purple-900', textSub: 'text-purple-800' }
  };

  // Agrupa un listado de docs por % de IVA efectivo (13%, 4%, 2%, 1%, 0%…).
  // NC se restan del subtotal de su grupo.
  const particionarPorTarifa = (docs) => {
    const acc = () => ({ base: 0, exo: 0, iva: 0, cantFE: 0, cantNC: 0 });
    const grupos = {};
    docs.forEach(d => {
      const t = parseInt(d.tarifa) || 0;
      const key = String(t);
      if (!grupos[key]) grupos[key] = { tarifa: t, docs: [], sub: acc() };
      const mult = d.esNC ? -1 : 1;
      grupos[key].docs.push(d);
      grupos[key].sub.base += (parseFloat(d.base_imponible_crc) || 0) * mult;
      grupos[key].sub.exo += (parseFloat(d.exoneracion_crc) || 0) * mult;
      grupos[key].sub.iva += (parseFloat(d.impuesto_crc) || 0) * mult;
      if (d.esNC) grupos[key].sub.cantNC++; else grupos[key].sub.cantFE++;
    });
    return grupos;
  };

  // Estilo del grupo según la tarifa.
  const metaPorTarifa = (t) => {
    if (t >= 13) return { emoji: '🟢', bg: 'bg-emerald-50', bgHover: 'hover:bg-emerald-100', border: 'border-emerald-200', text: 'text-emerald-900', textSub: 'text-emerald-800' };
    if (t > 0)   return { emoji: '🟡', bg: 'bg-amber-50', bgHover: 'hover:bg-amber-100', border: 'border-amber-200', text: 'text-amber-900', textSub: 'text-amber-800' };
    return { emoji: '⚪', bg: 'bg-gray-50', bgHover: 'hover:bg-gray-100', border: 'border-gray-200', text: 'text-gray-900', textSub: 'text-gray-700' };
  };

  // Backwards-compat: las vistas viejas siguen llamando esto. Devuelve
  // estructura legacy {gravados, exonerados, subG, subE} usando la nueva clasificación.
  const particionarPorEstado = (docs) => {
    const grupos = particionarPorCategoria(docs);
    const gravados = [...grupos.gravadas13.docs, ...grupos.reducidas.docs, ...grupos.exentas.docs, ...grupos.noSujetas.docs];
    const exonerados = grupos.exoneradas.docs;
    const acc = (g) => ({ base: 0, exo: 0, iva: 0, neto: 0, cantFE: 0, cantNC: 0 });
    const subG = acc(); const subE = grupos.exoneradas.sub;
    ['gravadas13', 'reducidas', 'exentas', 'noSujetas'].forEach(k => {
      subG.base += grupos[k].sub.base; subG.exo += grupos[k].sub.exo;
      subG.iva += grupos[k].sub.iva; subG.neto += grupos[k].sub.neto;
      subG.cantFE += grupos[k].sub.cantFE; subG.cantNC += grupos[k].sub.cantNC;
    });
    return { gravados, exonerados, subG, subE };
  };

  // Export del detalle (Excel/PDF) que aparece en el header de las 3 vistas
  const exportarDetalle = async (vistaExport, formato) => {
    const key = `${vistaExport}-${formato}`;
    setExportingDetalle(key);
    try {
      const res = await gastosApi.exportIvaDetalle(vistaExport, periodo, formato);
      const ext = formato === 'pdf' ? 'pdf' : 'xlsx';
      const mime = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      const fn = vistaExport === 'debitos' ? 'Debito_Fiscal_Ventas'
              : vistaExport === 'creditos' ? 'Credito_Fiscal_Compras' : 'FEC_Autoliquidacion';
      a.download = `${fn}_${periodo}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${formato.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al exportar');
    } finally {
      setExportingDetalle(null);
    }
  };

  const cargarDatos = useCallback(async () => {
    if (!currentEmisor) return;

    setLoading(true);
    try {
      const response = await gastosApi.getIvaInteligenteResumen(periodo);
      setData(response.data.data);
    } catch (error) {
      console.error('Error cargando resumen IVA:', error);
    } finally {
      setLoading(false);
    }
  }, [currentEmisor, periodo]);

  const cargarDetalle = useCallback(async (tipo) => {
    if (!currentEmisor) return;

    setLoadingDetalle(true);
    try {
      let response;
      if (tipo === 'debitos') {
        response = await gastosApi.getDetalleDebitos(periodo);
      } else if (tipo === 'creditos') {
        response = await gastosApi.getDetalleCreditos(periodo);
      } else if (tipo === 'fec') {
        response = await gastosApi.getDetalleFEC(periodo);
      }
      setDetalleData(response?.data?.data || null);
    } catch (error) {
      console.error('Error cargando detalle:', error);
      setDetalleData(null);
    } finally {
      setLoadingDetalle(false);
    }
  }, [currentEmisor, periodo]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  useEffect(() => {
    if (vista !== 'dashboard') {
      cargarDetalle(vista);
    }
  }, [vista, periodo, cargarDetalle]);

  const cambiarPeriodo = (direccion) => {
    const [year, month] = periodo.split('-').map(Number);
    let newMonth = month + direccion;
    let newYear = year;

    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }

    setPeriodo(`${newYear}-${String(newMonth).padStart(2, '0')}`);
    setExpandedFactura(null);
    setEditingLinea(null);
  };

  const categorizarAuto = async () => {
    setCategorizando(true);
    try {
      const response = await gastosApi.categorizarAutomatico();
      if (response.data.success) {
        const n = response.data.data.categorizadas;
        toast.success(`${n} ${n === 1 ? 'línea categorizada' : 'líneas categorizadas'} automáticamente`);
        cargarDatos();
        if (vista === 'creditos') {
          cargarDetalle('creditos');
        }
      }
    } catch (error) {
      console.error('Error en categorización automática:', error);
      toast.error('Error al categorizar: ' + (error.response?.data?.error || error.message));
    } finally {
      setCategorizando(false);
    }
  };

  const handleCategorizarLinea = async (lineaId) => {
    try {
      const response = await gastosApi.categorizarLinea(lineaId, editForm);
      if (response.data.success) {
        setEditingLinea(null);
        setEditForm({});
        cargarDetalle('creditos');
        cargarDatos();
      }
    } catch (error) {
      console.error('Error categorizando línea:', error);
      toast.error('Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const startEditLinea = (linea) => {
    setEditingLinea(linea.id);
    setEditForm({
      tipo_movimiento: linea.tipo_movimiento || '01',
      clase: linea.clase || 'B',
      identificacion: linea.identificacion || 'N',
      tarifa_iva_categoria: linea.tarifa_iva_categoria || '13'
    });
  };

  const formatCurrency = (amount, moneda = 'CRC') => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: moneda,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatPeriodo = (p) => {
    const [year, month] = p.split('-');
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${meses[parseInt(month) - 1]} ${year}`;
  };

  if (!currentEmisor) {
    return (
      <EmptyState
        icon={Calculator}
        title="Seleccione una empresa"
        description="Use el selector en la parte superior para elegir la empresa."
      />
    );
  }

  if (loading) {
    return <LoadingPage />;
  }

  const saldoTipo = data?.saldo?.tipo || 'neutro';
  const saldoColor = saldoTipo === 'pagar' ? 'text-red-600' : saldoTipo === 'favor' ? 'text-emerald-600' : 'text-gray-600';
  const saldoBg = saldoTipo === 'pagar' ? 'bg-red-50 border-red-200' : saldoTipo === 'favor' ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200';

  return (
    <div className="space-y-6">
      {/* Header con navegación de período y toggle de vista */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          {/* Toggle vista */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1 mr-2">
            <button
              onClick={() => setVista('dashboard')}
              className={clsx(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                vista === 'dashboard' ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-900"
              )}
            >
              <LayoutDashboard className="h-4 w-4" />
              Resumen
            </button>
            <button
              onClick={() => setVista('debitos')}
              className={clsx(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                vista === 'debitos' ? "bg-white shadow text-red-600" : "text-gray-600 hover:text-gray-900"
              )}
            >
              <ArrowUpRight className="h-4 w-4" />
              Débitos
            </button>
            <button
              onClick={() => setVista('creditos')}
              className={clsx(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                vista === 'creditos' ? "bg-white shadow text-emerald-600" : "text-gray-600 hover:text-gray-900"
              )}
            >
              <ArrowDownRight className="h-4 w-4" />
              Créditos
            </button>
            {data?.fec?.cantidad > 0 && (
              <button
                onClick={() => setVista('fec')}
                className={clsx(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                  vista === 'fec' ? "bg-white shadow text-purple-600" : "text-gray-600 hover:text-gray-900"
                )}
              >
                <FileText className="h-4 w-4" />
                FEC
              </button>
            )}
          </div>

          {/* Filtro de período unificado (modo-mes only — IVA es por mes específico) */}
          <MonthRangePicker
            value={(() => {
              const [y, m] = periodo.split('-').map(Number);
              const last = new Date(y, m, 0).getDate();
              return { modo: 'mes', periodo, desde: `${periodo}-01`, hasta: `${periodo}-${String(last).padStart(2, '0')}` };
            })()}
            onChange={(v) => setPeriodo(v.periodo)}
            onRefresh={() => {
              cargarDatos();
              if (vista !== 'dashboard') cargarDetalle(vista);
            }}
            onlyMonth
          />
        </div>
      </div>

      {/* Vista Dashboard */}
      {vista === 'dashboard' && (
        <>
          {/* Cards principales: Débito - Crédito = Saldo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Débito Fiscal */}
            <div
              className="bg-white border-2 border-red-200 rounded-xl p-5 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setVista('debitos')}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <ArrowUpRight className="h-4 w-4 text-red-600" />
                  </div>
                  <span className="text-sm font-medium text-red-600">DÉBITO FISCAL</span>
                </div>
                <span className="text-xs text-gray-400">Ventas</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(data?.debito?.total)}</p>
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                <span>IVA cobrado en ventas</span>
                <ChevronRight className="h-3 w-3" />
              </div>
            </div>

            {/* Crédito Fiscal */}
            <div
              className="bg-white border-2 border-emerald-200 rounded-xl p-5 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setVista('creditos')}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <ArrowDownRight className="h-4 w-4 text-emerald-600" />
                  </div>
                  <span className="text-sm font-medium text-emerald-600">CRÉDITO FISCAL</span>
                </div>
                <span className="text-xs text-gray-400">Compras</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(data?.credito?.total)}</p>
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                <span>IVA acreditable de compras</span>
                <ChevronRight className="h-3 w-3" />
              </div>
            </div>

            {/* Saldo */}
            <div className={clsx("border-2 rounded-xl p-5", saldoBg)}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={clsx(
                    "h-8 w-8 rounded-lg flex items-center justify-center",
                    saldoTipo === 'pagar' ? 'bg-red-200' : saldoTipo === 'favor' ? 'bg-emerald-200' : 'bg-gray-200'
                  )}>
                    {saldoTipo === 'pagar' ? (
                      <TrendingUp className="h-4 w-4 text-red-600" />
                    ) : saldoTipo === 'favor' ? (
                      <TrendingDown className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <Minus className="h-4 w-4 text-gray-600" />
                    )}
                  </div>
                  <span className={clsx("text-sm font-medium", saldoColor)}>
                    {saldoTipo === 'pagar' ? 'A PAGAR' : saldoTipo === 'favor' ? 'A FAVOR' : 'NEUTRO'}
                  </span>
                </div>
              </div>
              <p className={clsx("text-2xl font-bold", saldoColor)}>
                {formatCurrency(Math.abs(data?.saldo?.monto || 0))}
              </p>
              <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                <span>Débito - Crédito</span>
              </div>
            </div>
          </div>

          {/* Alerta AI - Líneas sin categorizar */}
          {data?.sinCategorizar?.lineas > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Zap className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      {data.sinCategorizar.lineas} líneas sin categorizar en {data.sinCategorizar.facturas} facturas
                    </p>
                    <p className="text-xs text-amber-600">
                      IVA pendiente de clasificar: {formatCurrency(data.sinCategorizar.montoIva)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setVista('creditos')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors"
                  >
                    <List className="h-4 w-4" />
                    Ver detalle
                  </button>
                  <button
                    onClick={categorizarAuto}
                    disabled={categorizando}
                    className={clsx(
                      "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                      categorizando
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                        : "bg-amber-600 text-white hover:bg-amber-700"
                    )}
                  >
                    {categorizando ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Categorizando...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4" />
                        Categorizar AI
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Separador visual */}
          <div className="py-4"></div>

          {/* Grid de 3 columnas: Débitos | Créditos | FEC (alineado con cards de arriba) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Columna 1: Débitos (izquierda) - alineado con card DÉBITO FISCAL */}
            <div className="lg:col-span-1">
              <ArbolD104Debitos periodo={periodo} formatCurrency={formatCurrency} />
            </div>

            {/* Columna 2: Créditos (centro) - alineado con card CRÉDITO FISCAL */}
            <div className="lg:col-span-1">
              <ArbolD104Creditos periodo={periodo} formatCurrency={formatCurrency} />
            </div>

            {/* Columna 3: FEC (derecha) - alineado con card SALDO */}
            <div className="lg:col-span-1">
              <ArbolD104FEC periodo={periodo} formatCurrency={formatCurrency} />
            </div>
          </div>

          {/* Prorrata info */}
          {data?.prorrata && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Percent className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    Prorrata IVA: {data.prorrata.porcentaje}%
                  </p>
                  <p className="text-xs text-gray-500">
                    {data.prorrata.porcentaje === 100
                      ? 'Sin actividades exentas - 100% acreditable'
                      : `${data.prorrata.porcentaje}% del IVA no identificado es acreditable`}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Vista Detalle Débitos */}
      {vista === 'debitos' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col h-[calc(var(--alto-contenido)-100px)]">
            <div className="shrink-0 p-4 border-b border-gray-100 bg-red-50 relative">
              {/* Izquierda absolute */}
              <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-3 z-10">
                <ArrowUpRight className="h-5 w-5 text-red-600 flex-shrink-0" />
                <h3 className="text-sm font-semibold text-red-900">
                  Detalle Débito Fiscal (Ventas)
                </h3>
                <AnulacionesCruzadasPulse periodo={periodo} formatCurrency={formatCurrency} />
              </div>
              {/* Derecha absolute */}
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-right z-10">
                <p className="text-sm font-bold text-red-600">{formatCurrency(data?.debito?.total)}</p>
                <p className="text-xs text-red-500">{detalleData?.totalFacturas || 0} facturas</p>
              </div>
              {/* Botones centrados al ancho completo de la pantalla */}
              <div className="flex items-center justify-center gap-2">
                <button onClick={() => exportarDetalle('debitos', 'excel')} disabled={!!exportingDetalle}
                  className="px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-white border-2 border-emerald-400 rounded-lg hover:bg-emerald-50 disabled:opacity-50 flex items-center gap-1.5"
                  title="Exportar a Excel">
                  {exportingDetalle === 'debitos-excel' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                  Excel
                </button>
                <button onClick={() => exportarDetalle('debitos', 'pdf')} disabled={!!exportingDetalle}
                  className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-white border-2 border-red-400 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
                  title="Exportar a PDF">
                  {exportingDetalle === 'debitos-pdf' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  PDF
                </button>
              </div>
            </div>

            {loadingDetalle ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="overflow-auto flex-1 min-h-0">
                {(() => {
                  // Combinar facturas y NC en una sola lista, ordenadas por fecha
                  const todosDocumentos = [
                    ...(detalleData?.facturas || []).map(f => ({ ...f, esNC: false })),
                    ...(detalleData?.notasCredito || []).map(nc => ({ ...nc, esNC: true }))
                  ].sort((a, b) => new Date(b.fecha_emision) - new Date(a.fecha_emision));

                  const totalFacturas = detalleData?.facturas?.length || 0;
                  const totalNC = detalleData?.notasCredito?.length || 0;

                  return (
                    <table className="w-full text-sm min-w-[600px]">
                      <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Fecha</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Tipo</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Documento</th>
                          <th className="px-4 py-3 text-left font-medium text-gray-600">Cliente</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Base Imponible</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">Exoneración</th>
                          <th className="px-4 py-3 text-right font-medium text-gray-600">IVA</th>
                          <th className="px-4 py-3 text-right font-medium text-red-600">A Débito</th>
                          <th className="px-4 py-3 text-center font-medium text-gray-600">Tarifa</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {todosDocumentos.length === 0 && (
                          <tr>
                            <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                              No hay documentos de venta en este período
                            </td>
                          </tr>
                        )}
                        {todosDocumentos.length > 0 && (() => {
                          // Agrupar por % de IVA efectivo (13%, 4%, 2%, 1%, 0%…).
                          const grupos = particionarPorTarifa(todosDocumentos);
                          const renderFila = (doc) => (
                            <tr key={`${doc.esNC ? 'nc' : 'fe'}-${doc.id}`} className={doc.esNC ? "hover:bg-orange-50 bg-orange-50/30" : "hover:bg-gray-50"}>
                              <td className="px-4 py-3 text-gray-900">{formatDate(doc.fecha_emision)}</td>
                              <td className="px-4 py-3">
                                {doc.esNC ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">NC</span>
                                ) : (
                                  <span className={clsx(
                                    "px-2 py-0.5 rounded text-xs font-medium",
                                    doc.tipo_documento === '01' || doc.tipo_documento === 'FE' ? "bg-blue-100 text-blue-700" :
                                    doc.tipo_documento === '04' || doc.tipo_documento === 'TE' ? "bg-gray-100 text-gray-700" :
                                    "bg-purple-100 text-purple-700"
                                  )}>
                                    {doc.tipo_documento === '01' || doc.tipo_documento === 'FE' ? 'FE' :
                                     doc.tipo_documento === '04' || doc.tipo_documento === 'TE' ? 'TE' : doc.tipo_documento}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-600 font-mono text-xs">{doc.numero_consecutivo}</td>
                              <td className="px-4 py-3 text-gray-900">{doc.cliente_nombre || 'Sin nombre'}</td>
                              <td className={clsx("px-4 py-3 text-right", doc.esNC ? "text-orange-600" : "text-gray-600")}>
                                {doc.esNC ? `(${formatCurrency(doc.base_imponible_crc)})` : formatCurrency(doc.base_imponible_crc)}
                              </td>
                              <td className={clsx("px-4 py-3 text-right", doc.esNC ? "text-orange-600" : "text-gray-600")}>
                                {formatCurrency(doc.exoneracion_crc || 0)}
                              </td>
                              <td className={clsx("px-4 py-3 text-right", doc.esNC ? "text-orange-600" : "text-gray-600")}>
                                {doc.esNC ? `(${formatCurrency(doc.impuesto_crc)})` : formatCurrency(doc.impuesto_crc)}
                              </td>
                              <td className={clsx("px-4 py-3 text-right font-medium", doc.esNC ? "text-orange-600" : "text-red-600")}>
                                {doc.esNC ? `(${formatCurrency(doc.impuesto_crc)})` : formatCurrency(doc.impuesto_crc)}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={clsx(
                                  "px-2 py-0.5 rounded text-xs font-medium",
                                  doc.esNC ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-700"
                                )}>
                                  {doc.tarifa || 0}%
                                </span>
                              </td>
                            </tr>
                          );
                          // Orden por tarifa descendente (13% arriba, 0% al final).
                          const orden = Object.keys(grupos).sort((a, b) => parseInt(b) - parseInt(a));
                          return (
                            <>
                              {orden.map(key => {
                                const grupo = grupos[key];
                                if (grupo.docs.length === 0) return null;
                                const meta = metaPorTarifa(grupo.tarifa);
                                const stateKey = `deb_tarifa_${key}`;
                                const expandido = grupoExpandido[stateKey] !== false;
                                return (
                                  <React.Fragment key={key}>
                                    <tr
                                      className={`${meta.bg} ${meta.bgHover} cursor-pointer select-none border-y ${meta.border}`}
                                      onClick={() => toggleGrupo(stateKey)}
                                    >
                                      <td colSpan={9} className={`px-4 py-2 text-sm font-semibold ${meta.text}`}>
                                        <span className="inline-block w-4">{expandido ? '▼' : '▶'}</span>
                                        {meta.emoji} IVA {grupo.tarifa}% — {grupo.sub.cantFE} FE{grupo.sub.cantNC > 0 ? `, ${grupo.sub.cantNC} NC` : ''}
                                        <span className={`ml-4 text-xs font-normal ${meta.textSub}`}>
                                          Base: <strong>{formatCurrency(grupo.sub.base)}</strong>
                                          {grupo.sub.exo > 0 && <> · Exonerado: <strong>{formatCurrency(grupo.sub.exo)}</strong></>}
                                          {grupo.sub.iva > 0 && <> · IVA: <strong>{formatCurrency(grupo.sub.iva)}</strong></>}
                                        </span>
                                      </td>
                                    </tr>
                                    {expandido && grupo.docs.map(renderFila)}
                                  </React.Fragment>
                                );
                              })}
                            </>
                          );
                        })()}
                      </tbody>
                      {todosDocumentos.length > 0 && (() => {
                        // Subtotales por % de IVA + total general (NC restando).
                        const grupos = particionarPorTarifa(todosDocumentos);
                        const orden = Object.keys(grupos).sort((a, b) => parseInt(b) - parseInt(a));
                        const total = { base: 0, exo: 0, iva: 0, cantFE: 0, cantNC: 0 };
                        orden.forEach(k => {
                          total.base += grupos[k].sub.base;
                          total.exo += grupos[k].sub.exo;
                          total.iva += grupos[k].sub.iva;
                          total.cantFE += grupos[k].sub.cantFE;
                          total.cantNC += grupos[k].sub.cantNC;
                        });
                        const Row = ({ label, sub, bg, txt }) => (
                          <tr className={`${bg} border-t border-gray-200`}>
                            <td colSpan={4} className={`px-4 py-2 text-right font-semibold ${txt}`}>
                              {label} ({sub.cantFE} FE{sub.cantNC > 0 ? `, ${sub.cantNC} NC` : ''})
                            </td>
                            <td className={`px-4 py-2 text-right font-bold ${txt}`}>{formatCurrency(sub.base)}</td>
                            <td className={`px-4 py-2 text-right font-bold ${txt}`}>{formatCurrency(sub.exo)}</td>
                            <td className={`px-4 py-2 text-right font-bold ${txt}`}>{formatCurrency(sub.iva)}</td>
                            <td className={`px-4 py-2 text-right font-bold ${txt}`}>{formatCurrency(sub.iva)}</td>
                            <td></td>
                          </tr>
                        );
                        return (
                          <tfoot className="sticky bottom-0 z-10">
                            {orden.map(key => {
                              const grupo = grupos[key];
                              if (grupo.sub.cantFE + grupo.sub.cantNC === 0) return null;
                              const meta = metaPorTarifa(grupo.tarifa);
                              return <Row key={key} label={`Subtotal IVA ${grupo.tarifa}%`} sub={grupo.sub} bg={meta.bg} txt={meta.textSub} />;
                            })}
                            <Row label="TOTAL" sub={total} bg="bg-red-50 border-t-2 border-red-200" txt="text-red-700" />
                          </tfoot>
                        );
                      })()}
                    </table>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vista Detalle Créditos */}
      {vista === 'creditos' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col h-[calc(var(--alto-contenido)-100px)]">
            <div className="shrink-0 p-4 border-b border-gray-100 bg-emerald-50 relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-3 z-10">
                <ArrowDownRight className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <h3 className="text-sm font-semibold text-emerald-900">
                  Detalle Crédito Fiscal (Compras)
                </h3>
              </div>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-right z-10">
                <p className="text-sm font-bold text-emerald-600">{formatCurrency(data?.credito?.total)}</p>
                <p className="text-xs text-emerald-500">{detalleData?.totalFacturas || 0} facturas</p>
              </div>
              <div className="flex items-center justify-center gap-2">
                <button onClick={() => exportarDetalle('creditos', 'excel')} disabled={!!exportingDetalle}
                  className="px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-white border-2 border-emerald-400 rounded-lg hover:bg-emerald-50 disabled:opacity-50 flex items-center gap-1.5"
                  title="Exportar a Excel">
                  {exportingDetalle === 'creditos-excel' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                  Excel
                </button>
                <button onClick={() => exportarDetalle('creditos', 'pdf')} disabled={!!exportingDetalle}
                  className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-white border-2 border-red-400 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
                  title="Exportar a PDF">
                  {exportingDetalle === 'creditos-pdf' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  PDF
                </button>
              </div>
            </div>

            {loadingDetalle ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="overflow-auto flex-1 min-h-0">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Fecha</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Tipo</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Documento</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Proveedor</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Base Imponible</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Exoneración</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">IVA</th>
                      <th className="px-4 py-3 text-right font-medium text-emerald-600">A Crédito</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">Tarifa</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(!detalleData?.facturas || detalleData.facturas.length === 0) && (!detalleData?.notasCredito || detalleData.notasCredito.length === 0) && (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                          No hay facturas de compra en este período
                        </td>
                      </tr>
                    )}
                    {((detalleData?.facturas?.length || 0) + (detalleData?.notasCredito?.length || 0)) > 0 && (() => {
                      const docs = [
                        ...(detalleData?.facturas || []).map(f => ({ ...f, esNC: false })),
                        ...(detalleData?.notasCredito || []).map(nc => ({ ...nc, esNC: true }))
                      ].sort((a, b) => new Date(b.fecha_emision) - new Date(a.fecha_emision));
                      const grupos = particionarPorCategoria(docs);
                      const renderFila = (f) => (
                        <tr key={`${f.esNC ? 'nc' : 'fe'}-${f.id}`} className={f.esNC ? "hover:bg-red-50 bg-red-50/30" : "hover:bg-gray-50"}>
                          <td className="px-4 py-3 text-gray-900">{formatDate(f.fecha_emision)}</td>
                          <td className="px-4 py-3">
                            <span className={clsx(
                              "px-2 py-0.5 rounded text-xs font-medium",
                              f.esNC ? "bg-red-100 text-red-700" :
                              f.tipo_documento === '01' || f.tipo_documento === 'FE' ? "bg-blue-100 text-blue-700" :
                              f.tipo_documento === '04' || f.tipo_documento === 'TE' ? "bg-gray-100 text-gray-700" :
                              "bg-purple-100 text-purple-700"
                            )}>
                              {f.esNC ? 'NC' :
                               f.tipo_documento === '01' || f.tipo_documento === 'FE' ? 'FE' :
                               f.tipo_documento === '04' || f.tipo_documento === 'TE' ? 'TE' : f.tipo_documento}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 font-mono text-xs">{f.numero_consecutivo}</td>
                          <td className="px-4 py-3 text-gray-900">{f.proveedor_nombre || 'Sin proveedor'}</td>
                          <td className={`px-4 py-3 text-right ${f.esNC ? 'text-red-600' : 'text-gray-600'}`}>
                            {f.esNC ? '-' : ''}{formatCurrency(f.base_imponible_crc)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600">
                            {formatCurrency(f.exoneracion_crc || 0)}
                          </td>
                          <td className={`px-4 py-3 text-right ${f.esNC ? 'text-red-600' : 'text-gray-600'}`}>
                            {f.esNC ? '-' : ''}{formatCurrency(f.impuesto_crc)}
                          </td>
                          <td className={`px-4 py-3 text-right font-medium ${f.esNC ? 'text-red-600' : 'text-emerald-600'}`}>
                            {f.esNC ? '-' : ''}{formatCurrency(f.iva_acreditable_crc || f.impuesto_crc)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-medium">
                              {f.tarifa ? `${f.tarifa}%` : '-'}
                            </span>
                          </td>
                        </tr>
                      );
                      const orden = ['gravadas13', 'reducidas', 'exoneradas', 'exentas', 'noSujetas'];
                      return (
                        <>
                          {orden.map(catKey => {
                            const grupo = grupos[catKey];
                            if (grupo.docs.length === 0) return null;
                            const meta = CATEGORIA_META[catKey];
                            const stateKey = `cred_${catKey}`;
                            const expandido = grupoExpandido[stateKey];
                            return (
                              <React.Fragment key={catKey}>
                                <tr
                                  className={`${meta.bg} ${meta.bgHover} cursor-pointer select-none border-y ${meta.border}`}
                                  onClick={() => toggleGrupo(stateKey)}
                                >
                                  <td colSpan={9} className={`px-4 py-2 text-sm font-semibold ${meta.text}`}>
                                    <span className="inline-block w-4">{expandido ? '▼' : '▶'}</span>
                                    {meta.emoji} {meta.label} — {grupo.sub.cantFE} FE{grupo.sub.cantNC > 0 ? `, ${grupo.sub.cantNC} NC` : ''}
                                    <span className={`ml-4 text-xs font-normal ${meta.textSub}`}>
                                      Base: <strong>{formatCurrency(grupo.sub.base)}</strong>
                                      {grupo.sub.exo > 0 && <> · Exonerado: <strong>{formatCurrency(grupo.sub.exo)}</strong></>}
                                      {grupo.sub.neto > 0 && <> · IVA acreditable: <strong>{formatCurrency(grupo.sub.neto)}</strong></>}
                                      <span className="text-gray-500 ml-2">— {meta.desc}</span>
                                    </span>
                                  </td>
                                </tr>
                                {expandido && grupo.docs.map(renderFila)}
                              </React.Fragment>
                            );
                          })}
                        </>
                      );
                    })()}
                  </tbody>
                  {((detalleData?.facturas?.length || 0) + (detalleData?.notasCredito?.length || 0)) > 0 && (() => {
                    const docs = [
                      ...(detalleData?.facturas || []).map(f => ({ ...f, esNC: false })),
                      ...(detalleData?.notasCredito || []).map(nc => ({ ...nc, esNC: true }))
                    ];
                    const grupos = particionarPorCategoria(docs);
                    const orden = ['gravadas13', 'reducidas', 'exoneradas', 'exentas', 'noSujetas'];
                    const total = { base: 0, exo: 0, iva: 0, neto: 0, cantFE: 0, cantNC: 0 };
                    orden.forEach(k => {
                      total.base += grupos[k].sub.base; total.exo += grupos[k].sub.exo;
                      total.iva += grupos[k].sub.iva; total.neto += grupos[k].sub.neto;
                      total.cantFE += grupos[k].sub.cantFE; total.cantNC += grupos[k].sub.cantNC;
                    });
                    const Row = ({ label, sub, bg, txt, txtCred }) => (
                      <tr className={`${bg} border-t border-gray-200`}>
                        <td colSpan={4} className={`px-4 py-2 text-right font-semibold ${txt}`}>
                          {label} ({sub.cantFE} FE{sub.cantNC > 0 ? `, ${sub.cantNC} NC` : ''})
                        </td>
                        <td className={`px-4 py-2 text-right font-bold ${txt}`}>{formatCurrency(sub.base)}</td>
                        <td className={`px-4 py-2 text-right font-bold ${txt}`}>{formatCurrency(sub.exo)}</td>
                        <td className={`px-4 py-2 text-right font-bold ${txt}`}>{formatCurrency(sub.iva)}</td>
                        <td className={`px-4 py-2 text-right font-bold ${txtCred}`}>{formatCurrency(sub.neto)}</td>
                        <td></td>
                      </tr>
                    );
                    return (
                      <tfoot className="sticky bottom-0 z-10">
                        {orden.map(catKey => {
                          const grupo = grupos[catKey];
                          if (grupo.sub.cantFE + grupo.sub.cantNC === 0) return null;
                          const meta = CATEGORIA_META[catKey];
                          return <Row key={catKey} label={`Subtotal ${meta.label}`} sub={grupo.sub} bg={meta.bg} txt={meta.textSub} txtCred={meta.textSub} />;
                        })}
                        <Row label="TOTAL" sub={total} bg="bg-emerald-100 border-t-2 border-emerald-300" txt="text-gray-800" txtCred="text-emerald-700" />
                      </tfoot>
                    );
                  })()}
                </table>
              </div>
            )}

            {/* NC ya integradas en la tabla principal */}
          </div>
        </div>
      )}

      {/* Vista Detalle FEC */}
      {vista === 'fec' && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col h-[calc(var(--alto-contenido)-100px)]">
            <div className="shrink-0 p-4 border-b border-gray-100 bg-purple-50 relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-3 z-10">
                <FileText className="h-5 w-5 text-purple-600 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-purple-900">
                    Facturas Electrónicas de Compra (FEC)
                  </h3>
                  <p className="text-xs text-purple-600">
                    Autoliquidación IVA y Crédito Fiscal
                  </p>
                </div>
              </div>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-right z-10">
                <p className="text-sm font-bold text-purple-600">
                  {detalleData?.totalFECs || 0} FECs
                </p>
              </div>
              <div className="flex items-center justify-center gap-2">
                <button onClick={() => exportarDetalle('fec', 'excel')} disabled={!!exportingDetalle}
                  className="px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-white border-2 border-emerald-400 rounded-lg hover:bg-emerald-50 disabled:opacity-50 flex items-center gap-1.5"
                  title="Exportar a Excel">
                  {exportingDetalle === 'fec-excel' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                  Excel
                </button>
                <button onClick={() => exportarDetalle('fec', 'pdf')} disabled={!!exportingDetalle}
                  className="px-3 py-1.5 text-xs font-semibold text-red-700 bg-white border-2 border-red-400 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
                  title="Exportar a PDF">
                  {exportingDetalle === 'fec-pdf' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  PDF
                </button>
              </div>
            </div>

            {loadingDetalle ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="overflow-auto flex-1 min-h-0">
                <table className="w-full text-sm min-w-[600px]">
                  <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Fecha</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Tipo</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Documento</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Proveedor</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">País</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Servicio</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Base Imponible</th>
                      <th className="px-4 py-3 text-right font-medium text-red-600">Débito</th>
                      <th className="px-4 py-3 text-right font-medium text-emerald-600">Crédito</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {detalleData?.facturas?.map((f) => (
                      <tr key={f.id} className={`hover:bg-gray-50 ${f.fec_solo_credito ? 'bg-blue-50/30' : ''}`}>
                        <td className="px-4 py-3 text-gray-900">{formatDate(f.fecha_emision)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${f.fec_solo_credito ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                            {f.fec_solo_credito ? 'Transfronterizo' : 'Autoliquidación'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 font-mono text-xs">{f.numero_consecutivo}</td>
                        <td className="px-4 py-3 text-gray-900">{f.proveedor_nombre || 'Sin nombre'}</td>
                        <td className="px-4 py-3 text-gray-600">{f.proveedor_pais || '-'}</td>
                        <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={f.detalle_servicio}>
                          {f.detalle_servicio || '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {formatCurrency(f.base_imponible_crc)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-red-600">
                          {f.fec_solo_credito ? '-' : formatCurrency(f.debito_crc)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-emerald-600">
                          {formatCurrency(f.credito_crc)}
                        </td>
                      </tr>
                    ))}
                    {(!detalleData?.facturas || detalleData.facturas.length === 0) && (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                          No hay FECs en este período
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {detalleData?.facturas?.length > 0 && (() => {
                    const fecsAuto = detalleData.facturas.filter(f => !f.fec_solo_credito);
                    const fecsSolo = detalleData.facturas.filter(f => f.fec_solo_credito);
                    const totalBaseAuto = fecsAuto.reduce((sum, f) => sum + (f.base_imponible_crc || 0), 0);
                    const totalBaseSolo = fecsSolo.reduce((sum, f) => sum + (f.base_imponible_crc || 0), 0);
                    const totalDebitoAuto = fecsAuto.reduce((sum, f) => sum + (f.debito_crc || 0), 0);
                    const totalCreditoAuto = fecsAuto.reduce((sum, f) => sum + (f.credito_crc || 0), 0);
                    const totalCreditoSolo = fecsSolo.reduce((sum, f) => sum + (f.credito_crc || 0), 0);
                    const totalDebito = totalDebitoAuto;
                    const totalCredito = totalCreditoAuto + totalCreditoSolo;
                    // Saldo: positivo = a favor (más crédito), negativo = a pagar (más débito)
                    const saldoNeto = totalCredito - totalDebito;

                    return (
                      <tfoot className="bg-purple-50 border-t border-purple-200 sticky bottom-0 z-10">
                        {fecsAuto.length > 0 && (
                          <tr className="bg-purple-50/50">
                            <td colSpan={6} className="px-4 py-2 text-right text-sm text-purple-700">
                              Autoliquidación ({fecsAuto.length} FECs):
                            </td>
                            <td className="px-4 py-2 text-right font-medium text-gray-700">
                              {formatCurrency(totalBaseAuto)}
                            </td>
                            <td className="px-4 py-2 text-right font-medium text-red-600">
                              {formatCurrency(totalDebitoAuto)}
                            </td>
                            <td className="px-4 py-2 text-right font-medium text-emerald-600">
                              {formatCurrency(totalCreditoAuto)}
                            </td>
                          </tr>
                        )}
                        {fecsSolo.length > 0 && (
                          <tr className="bg-blue-50/50">
                            <td colSpan={6} className="px-4 py-2 text-right text-sm text-blue-700">
                              IVA Transfronterizo ({fecsSolo.length} FECs):
                            </td>
                            <td className="px-4 py-2 text-right font-medium text-gray-700">
                              {formatCurrency(totalBaseSolo)}
                            </td>
                            <td className="px-4 py-2 text-right font-medium text-gray-400">
                              -
                            </td>
                            <td className="px-4 py-2 text-right font-medium text-emerald-600">
                              {formatCurrency(totalCreditoSolo)}
                            </td>
                          </tr>
                        )}
                        <tr className="font-bold border-t border-purple-300">
                          <td colSpan={6} className="px-4 py-3 text-right text-purple-800">
                            TOTAL:
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900">
                            {formatCurrency(totalBaseAuto + totalBaseSolo)}
                          </td>
                          <td className="px-4 py-3 text-right text-red-600">
                            {formatCurrency(totalDebito)}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-600">
                            {formatCurrency(totalCredito)}
                          </td>
                        </tr>
                        <tr className="bg-gray-100">
                          <td colSpan={8} className="px-4 py-2 text-right text-sm text-gray-700">
                            Saldo Neto:
                          </td>
                          <td colSpan={2} className={`px-4 py-2 text-center font-bold ${saldoNeto > 0 ? 'text-emerald-700' : saldoNeto < 0 ? 'text-red-700' : 'text-purple-800'}`}>
                            {saldoNeto === 0 ? '₡0' : (
                              <span>
                                {formatCurrency(Math.abs(saldoNeto))}
                                <span className="text-xs font-normal ml-1">
                                  {saldoNeto > 0 ? 'a favor' : 'a pagar'}
                                </span>
                              </span>
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    );
                  })()}
                </table>
              </div>
            )}
          </div>

          {/* Explicación FEC */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-purple-800">
                <p className="font-medium mb-1">Tipos de FEC</p>
                <ul className="text-purple-700 space-y-1">
                  <li><strong>Autoliquidación:</strong> Compras a proveedores sin FE (régimen simplificado, extranjeros, tiquetes). El IVA se reporta como débito y crédito fiscal simultáneamente = efecto neto ₡0.</li>
                  <li><strong>IVA Transfronterizo:</strong> Servicios digitales donde el banco ya retuvo el IVA. Solo genera crédito fiscal (el débito ya fue pagado al banco).</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sin datos */}
      {!data && !loading && (
        <EmptyState
          icon={Calculator}
          title="Sin datos para este período"
          description="No hay facturas de compra o venta registradas en este período."
        />
      )}
    </div>
  );
}
